/**
 * Shared senior-UX primitives for the org onboarding flows (referrer,
 * employer, sponsor) and the admin approval surface.
 *
 * Pure helpers only — no React, no DOM — so they are unit-testable under
 * node. UI components live in src/components/onboarding/.
 */

import { COUNTRIES } from './constants';
import { isValidEmail, isValidPhone, isValidUrl } from './onboarding';
import { PHONE_COUNTRIES } from './phoneCountries';

/* ------------------------------------------------------------------ */
/* Stepper gating                                                      */
/* ------------------------------------------------------------------ */

/**
 * The wizard stepper (Account → Organization → Review) is part of the
 * new-user creation journey ONLY. It must never render on pending
 * screens, sign-in, role selection, or for returning users.
 *
 * The org onboarding wizards (ReferrerOnboarding, EmployerOnboarding,
 * SponsorOnboarding) are themselves mounted by App.tsx exclusively in
 * the new-user / incomplete-profile branches — that mount is the gate.
 * WizardStepper asserts the same contract via the required `isNewUser`
 * prop: it renders null when false, so a future caller can never leak the
 * stepper onto a returning-user screen by accident.
 */
export function isWizardStepperVisible(isNewUser: boolean): boolean {
  return isNewUser === true;
}

/* ------------------------------------------------------------------ */
/* Draft key                                                           */
/* ------------------------------------------------------------------ */

/**
 * localStorage key for the per-step onboarding draft. Keyed per account
 * (uid) + org type so drafts never leak across accounts sharing a browser.
 */
export function buildOnboardingDraftKey(
  orgType: 'referrer' | 'employer' | 'sponsor',
  userId: string,
): string {
  return `onboarding:draft:${orgType}:${userId}`;
}

/* ------------------------------------------------------------------ */
/* Country smart-default from browser locale                            */
/* ------------------------------------------------------------------ */

/** Region code → country display name (must match COUNTRIES entries). */
const REGION_TO_COUNTRY: Record<string, string> = {
  US: 'United States',
  CA: 'Canada',
  GB: 'United Kingdom',
  JM: 'Jamaica',
  NG: 'Nigeria',
  GH: 'Ghana',
  KE: 'Kenya',
  ZA: 'South Africa',
  IN: 'India',
  PK: 'Pakistan',
  BD: 'Bangladesh',
  PH: 'Philippines',
  AU: 'Australia',
  NZ: 'New Zealand',
  IE: 'Ireland',
  FR: 'France',
  DE: 'Germany',
  ES: 'Spain',
  IT: 'Italy',
  PT: 'Portugal',
  NL: 'Netherlands',
  BE: 'Belgium',
  CH: 'Switzerland',
  SE: 'Sweden',
  NO: 'Norway',
  DK: 'Denmark',
  FI: 'Finland',
  PL: 'Poland',
  BR: 'Brazil',
  MX: 'Mexico',
  CO: 'Colombia',
  AR: 'Argentina',
  CL: 'Chile',
  PE: 'Peru',
  TT: 'Trinidad and Tobago',
  BB: 'Barbados',
  BS: 'Bahamas',
  DO: 'Dominican Republic',
  HT: 'Haiti',
  CU: 'Cuba',
  AE: 'United Arab Emirates',
  SA: 'Saudi Arabia',
  QA: 'Qatar',
  KW: 'Kuwait',
  EG: 'Egypt',
  MA: 'Morocco',
  SG: 'Singapore',
  MY: 'Malaysia',
  ID: 'Indonesia',
  TH: 'Thailand',
  VN: 'Vietnam',
  JP: 'Japan',
  KR: 'South Korea',
  CN: 'China',
  TW: 'Taiwan',
  HK: 'Hong Kong',
};

/**
 * Resolves a country display name from a BCP-47 locale (e.g. "en-JM").
 * Returns '' when the locale has no region or the region is not in the
 * supported country list. Callers must only apply the result while the
 * country field is still untouched.
 */
export function resolveDefaultCountry(locale?: string): string {
  if (!locale) return '';
  const parts = locale.split(/[-_]/);
  const region = (parts[1] || '').toUpperCase();
  if (!region) return '';
  const name = REGION_TO_COUNTRY[region];
  if (!name || !COUNTRIES.includes(name)) return '';
  return name;
}

/* ------------------------------------------------------------------ */
/* Phone country-code picker (built-in dial codes, no dependencies)    */
/* ------------------------------------------------------------------ */

export interface DialCode {
  code: string;
  label: string;
}

const CURATED_DIAL_CODES: DialCode[] = [
  { code: '+1', label: 'United States / Canada (+1)' },
  { code: '+1242', label: 'Bahamas (+1242)' },
  { code: '+1246', label: 'Barbados (+1246)' },
  { code: '+1264', label: 'Anguilla (+1264)' },
  { code: '+1268', label: 'Antigua and Barbuda (+1268)' },
  { code: '+1284', label: 'British Virgin Islands (+1284)' },
  { code: '+1340', label: 'US Virgin Islands (+1340)' },
  { code: '+1345', label: 'Cayman Islands (+1345)' },
  { code: '+1441', label: 'Bermuda (+1441)' },
  { code: '+1473', label: 'Grenada (+1473)' },
  { code: '+1649', label: 'Turks and Caicos (+1649)' },
  { code: '+1664', label: 'Montserrat (+1664)' },
  { code: '+1670', label: 'N. Mariana Islands (+1670)' },
  { code: '+1671', label: 'Guam (+1671)' },
  { code: '+1684', label: 'American Samoa (+1684)' },
  { code: '+1721', label: 'Sint Maarten (+1721)' },
  { code: '+1758', label: 'Saint Lucia (+1758)' },
  { code: '+1767', label: 'Dominica (+1767)' },
  { code: '+1784', label: 'St. Vincent (+1784)' },
  { code: '+1787', label: 'Puerto Rico (+1787)' },
  { code: '+1809', label: 'Dominican Republic (+1809)' },
  { code: '+1829', label: 'Dominican Republic (+1829)' },
  { code: '+1849', label: 'Dominican Republic (+1849)' },
  { code: '+1868', label: 'Trinidad and Tobago (+1868)' },
  { code: '+1869', label: 'Saint Kitts and Nevis (+1869)' },
  { code: '+1876', label: 'Jamaica (+1876)' },
  { code: '+44', label: 'United Kingdom (+44)' },
  { code: '+353', label: 'Ireland (+353)' },
  { code: '+33', label: 'France (+33)' },
  { code: '+34', label: 'Spain (+34)' },
  { code: '+39', label: 'Italy (+39)' },
  { code: '+49', label: 'Germany (+49)' },
  { code: '+31', label: 'Netherlands (+31)' },
  { code: '+32', label: 'Belgium (+32)' },
  { code: '+41', label: 'Switzerland (+41)' },
  { code: '+43', label: 'Austria (+43)' },
  { code: '+351', label: 'Portugal (+351)' },
  { code: '+46', label: 'Sweden (+46)' },
  { code: '+47', label: 'Norway (+47)' },
  { code: '+45', label: 'Denmark (+45)' },
  { code: '+358', label: 'Finland (+358)' },
  { code: '+48', label: 'Poland (+48)' },
  { code: '+234', label: 'Nigeria (+234)' },
  { code: '+233', label: 'Ghana (+233)' },
  { code: '+254', label: 'Kenya (+254)' },
  { code: '+27', label: 'South Africa (+27)' },
  { code: '+20', label: 'Egypt (+20)' },
  { code: '+212', label: 'Morocco (+212)' },
  { code: '+91', label: 'India (+91)' },
  { code: '+92', label: 'Pakistan (+92)' },
  { code: '+880', label: 'Bangladesh (+880)' },
  { code: '+63', label: 'Philippines (+63)' },
  { code: '+61', label: 'Australia (+61)' },
  { code: '+64', label: 'New Zealand (+64)' },
  { code: '+81', label: 'Japan (+81)' },
  { code: '+82', label: 'South Korea (+82)' },
  { code: '+86', label: 'China (+86)' },
  { code: '+852', label: 'Hong Kong (+852)' },
  { code: '+886', label: 'Taiwan (+886)' },
  { code: '+65', label: 'Singapore (+65)' },
  { code: '+60', label: 'Malaysia (+60)' },
  { code: '+62', label: 'Indonesia (+62)' },
  { code: '+66', label: 'Thailand (+66)' },
  { code: '+84', label: 'Vietnam (+84)' },
  { code: '+971', label: 'United Arab Emirates (+971)' },
  { code: '+966', label: 'Saudi Arabia (+966)' },
  { code: '+974', label: 'Qatar (+974)' },
  { code: '+965', label: 'Kuwait (+965)' },
  { code: '+55', label: 'Brazil (+55)' },
  { code: '+52', label: 'Mexico (+52)' },
  { code: '+57', label: 'Colombia (+57)' },
  { code: '+54', label: 'Argentina (+54)' },
  { code: '+56', label: 'Chile (+56)' },
  { code: '+51', label: 'Peru (+51)' },
  { code: '+509', label: 'Haiti (+509)' },
];

/**
 * The full picker list: the curated entries above PLUS every dial code
 * from the canonical PHONE_COUNTRIES table (normalised: '+1-876' →
 * '+1876'), deduped by code. The curated subset alone was missing codes
 * for many selectable countries (Uganda +256, Tanzania +255, Rwanda
 * +250, Ethiopia +251, Cameroon +237, Senegal +221, Zambia +260,
 * Zimbabwe +263, …): a stored or pasted number in one of those
 * countries fell back to '+1' in splitDialCode and was silently
 * rewritten as a North American number that still passed validation.
 */
export const DIAL_CODES: DialCode[] = (() => {
  const byCode = new Map<string, DialCode>();
  for (const entry of CURATED_DIAL_CODES) byCode.set(entry.code, entry);
  for (const c of PHONE_COUNTRIES) {
    const code = c.dial.replace('-', '');
    if (!byCode.has(code)) byCode.set(code, { code, label: `${c.name} (${code})` });
  }
  return [...byCode.values()];
})();

/**
 * Splits a stored phone value into its dial-code prefix and the national
 * remainder, matching the LONGEST known dial code. Falls back to '+1'.
 */
export function splitDialCode(phone: string): { dialCode: string; national: string } {
  const compact = (phone || '').replace(/[\s\-.()]/g, '');
  if (!compact.startsWith('+')) return { dialCode: '+1', national: compact };
  const sorted = [...DIAL_CODES].sort((a, b) => b.code.length - a.code.length);
  for (const entry of sorted) {
    if (compact.startsWith(entry.code)) {
      return { dialCode: entry.code, national: compact.slice(entry.code.length) };
    }
  }
  return { dialCode: '+1', national: compact.replace(/^\+/, '') };
}

/* ------------------------------------------------------------------ */
/* Inline field validation                                             */
/* ------------------------------------------------------------------ */

export type FieldRule = 'required' | 'email' | 'phone' | 'url' | 'optional-url';

/**
 * Validates a single field value for inline (as-you-type / onBlur)
 * feedback. Returns '' when valid, otherwise the message to display.
 * Server-side validation remains authoritative — this is UX only.
 */
export function validateField(rule: FieldRule, value: string, label: string): string {
  const v = (value || '').trim();
  switch (rule) {
    case 'required':
      return v ? '' : `${label} is required.`;
    case 'email':
      if (!v) return `${label} is required.`;
      return isValidEmail(v) ? '' : 'Enter a valid email address.';
    case 'phone':
      if (!v) return `${label} is required.`;
      return isValidPhone(v) ? '' : 'Enter a valid phone number, e.g. +1-876-555-0199.';
    case 'url':
      if (!v) return `${label} is required.`;
      return isValidUrl(v) ? '' : 'Enter a valid website, e.g. https://example.com';
    case 'optional-url':
      return !v || isValidUrl(v) ? '' : 'Enter a valid website, e.g. https://example.com';
    default:
      return '';
  }
}

/* ------------------------------------------------------------------ */
/* "What happens next" copy (plain English, no marketing)              */
/* ------------------------------------------------------------------ */

export const NEXT_STEPS = [
  {
    title: 'Our team reviews your application',
    text: 'An administrator checks the details you submitted — usually within 1–2 business days.',
  },
  {
    title: 'You get an email notification',
    text: 'We email the address on your account the moment a decision is made.',
  },
  {
    title: 'Your dashboard unlocks on approval',
    text: 'Once approved, this screen automatically becomes your dashboard. Nothing else to do.',
  },
] as const;

/* ------------------------------------------------------------------ */
/* Pending status timeline                                             */
/* ------------------------------------------------------------------ */

export const PENDING_STAGES = ['Submitted', 'Under review', 'Approved'] as const;

/**
 * Maps an org application status to the timeline stage index.
 * - 'pending' (or any pre-decision state) → 1 (Under review, current)
 * - 'active' / 'approved' → 2 (Approved)
 * - 'rejected' → -1 (timeline not shown; the rejected UI takes over)
 */
export function getPendingStage(status?: string): number {
  const s = (status || '').toLowerCase();
  if (s === 'rejected') return -1;
  if (s === 'active' || s === 'approved') return 2;
  return 1;
}

/* ------------------------------------------------------------------ */
/* Admin approval snapshot: completeness + risk flags                   */
/* ------------------------------------------------------------------ */

/* The free/disposable email list + helpers live in
 * lib/disposableEmailDomains.ts (shared with the server — Self-Serve
 * Onboarding spec §2.1 P-9) and are re-exported here so existing imports
 * from this module keep working unchanged. */
export { DISPOSABLE_EMAIL_DOMAINS, emailDomain, isDisposableEmail } from './disposableEmailDomains';
import { emailDomain, isDisposableEmail } from './disposableEmailDomains';

/** Normalises an org name for duplicate comparison. */
export function normalizeOrgName(name?: string): string {
  return (name || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export type OrgType = 'employer' | 'referrer' | 'sponsor';

interface RequiredFieldDef {
  key: string;
  label: string;
  /** Extracts the raw value from the org record. */
  get: (item: any) => unknown;
  /** Whether the field counts as required for THIS record (e.g. contact name only for company referrers). */
  requiredFor?: (item: any) => boolean;
}

const hasValue = (v: unknown): boolean => {
  if (Array.isArray(v)) return v.filter(Boolean).length > 0;
  return typeof v === 'string' ? v.trim().length > 0 : v !== undefined && v !== null && v !== '';
};

const REQUIRED_FIELDS: Record<OrgType, RequiredFieldDef[]> = {
  employer: [
    { key: 'companyName', label: 'Company name', get: i => i.companyName || i.name },
    { key: 'contactName', label: 'Contact person', get: i => i.contactName || i.contactPerson },
    { key: 'contactTitle', label: 'Contact title', get: i => i.contactTitle },
    { key: 'contactPhone', label: 'Company phone', get: i => i.contactPhone || i.phone },
    { key: 'companyAddress', label: 'Address', get: i => i.companyAddress || i.address },
    { key: 'relatedPrograms', label: 'Programs', get: i => i.relatedPrograms },
  ],
  referrer: [
    { key: 'name', label: 'Name', get: i => i.name },
    {
      key: 'contactName',
      label: 'Contact person',
      get: i => i.contactName,
      requiredFor: i => i.type !== 'individual',
    },
    { key: 'contactPhone', label: 'Phone', get: i => i.contactPhone || i.phone },
    { key: 'address', label: 'Address', get: i => i.address || i.companyAddress },
    { key: 'relatedPrograms', label: 'Programs', get: i => i.relatedPrograms },
  ],
  sponsor: [
    { key: 'name', label: 'Organization name', get: i => i.name || i.companyName },
    { key: 'contactName', label: 'Contact name', get: i => i.contactName },
    { key: 'contactEmail', label: 'Contact email', get: i => i.contactEmail || i.email },
    { key: 'contactPhone', label: 'Contact phone', get: i => i.contactPhone || i.phone },
  ],
};

export interface ApprovalRiskFlag {
  code: 'duplicate-name' | 'disposable-email' | 'missing-fields';
  label: string;
  severity: 'warn' | 'error';
}

export interface ApprovalSnapshot {
  filled: number;
  total: number;
  pct: number;
  missing: string[];
  flags: ApprovalRiskFlag[];
}

/**
 * Builds the admin approval snapshot for one pending org record:
 * completeness percentage over required fields plus risk flags.
 *
 * @param nameIndex per-type list of {id, name} for ALL orgs (pending +
 *   active) — the server includes this in the pending-approvals payload.
 *   The record itself is excluded from duplicate matching.
 */
export function buildApprovalSnapshot(
  type: OrgType,
  item: any,
  nameIndex: { id: string; name: string }[] = [],
): ApprovalSnapshot {
  const defs = REQUIRED_FIELDS[type] || [];
  const applicable = defs.filter(d => !d.requiredFor || d.requiredFor(item));
  const missing: string[] = [];
  let filled = 0;
  for (const d of applicable) {
    if (hasValue(d.get(item))) filled += 1;
    else missing.push(d.label);
  }
  const total = applicable.length;
  const pct = total === 0 ? 100 : Math.round((filled / total) * 100);

  const flags: ApprovalRiskFlag[] = [];
  if (missing.length > 0) {
    flags.push({
      code: 'missing-fields',
      label: `Missing: ${missing.join(', ')}`,
      severity: 'error',
    });
  }

  const rawName = type === 'employer' ? item.companyName || item.name : item.name || item.companyName;
  const norm = normalizeOrgName(rawName);
  if (norm) {
    const dup = nameIndex.some(
      entry => entry.id !== item.id && normalizeOrgName(entry.name) === norm,
    );
    if (dup) {
      flags.push({
        code: 'duplicate-name',
        label: 'An organization with this name already exists.',
        severity: 'warn',
      });
    }
  }

  const email = item.contactEmail || item.email;
  if (email && isDisposableEmail(email)) {
    flags.push({
      code: 'disposable-email',
      label: `Contact email uses a free provider (${emailDomain(email)}).`,
      severity: 'warn',
    });
  }

  return { filled, total, pct, missing, flags };
}

/* ------------------------------------------------------------------ */
/* Enhanced Referrer Access — plain-English grant description          */
/* ------------------------------------------------------------------ */

/**
 * What the Enhanced Referrer Access opt-in actually grants. Rendered next
 * to the checkbox so the admin decision is informed, not ritual.
 */
export const ENHANCED_ACCESS_DESCRIPTION =
  'Enhanced access unlocks: team management (invite teammates and assign roles), commission tracking and payouts, the performance dashboard, and event access. Without it, the referrer gets the standard dashboard only.';

export const ENHANCED_ACCESS_BULLETS = [
  'Team management — invite teammates and assign roles',
  'Commission tracking and payouts',
  'Performance dashboard',
  'Event access',
] as const;

/* ------------------------------------------------------------------ */
/* Focus management                                                    */
/* ------------------------------------------------------------------ */

/**
 * Moves keyboard focus to the first text-like input inside a step
 * container. Called on step change so keyboard and screen-reader users
 * land at the start of the new step.
 */
export function focusFirstInput(container: HTMLElement | null): void {
  if (!container) return;
  const target = container.querySelector<HTMLElement>(
    'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])',
  );
  target?.focus({ preventScroll: false });
}
